/* Team: the people and ambulances that are yours.
 *
 * A nurse or driver who signs up in the Apkicare Partner app and says "I work
 * for this agency" appears here as a JOIN REQUEST. Until you confirm them they
 * cannot be sent anywhere, by you or anyone. Declining closes their sign-up with
 * your reason, and they may sign up again. A confirmed person who stops working
 * for you is recorded as a departure instead.
 *
 * Built like the console's Members screen: tiles, the waiting list first, one
 * table with chips and a search, and a drawer for the person.
 *
 * THE TILES DO SOMETHING (owner, 2026-10-23). Each takes you to its list:
 * "Asking to join" and "Ambulances" scroll to their section; "Confirmed staff"
 * shows everyone; "On duty" narrows the staff table to the people switched on
 * now. Click a lit tile again to clear it.
 *
 * AN AMBULANCE OPENS (owner, 2026-10-23). Its profile: what it is and carries,
 * whose it is (a driver who brought it, or your own fleet), the trip it is on
 * now, trips done, and its papers with their state and expiry (from
 * GET /agency/compliance). An owner or admin can take it off the road for
 * repair and put it back — refused while it is on a job.
 *
 * A NURSE'S SKILLS (owner, 2026-10-31) are on her drawer: she ticks them at
 * sign-up, an owner or admin adds or removes them here (staffSkills.tsx). */

import { useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  confirmStaff,
  createInvite,
  declineStaff,
  getCompliance,
  getTeam,
  listInvites,
  recordDeparture,
  revokeInvite,
  setVehicleStatus,
  withdrawDeparture,
  type StaffInvite,
  type StaffMember,
  type Vehicle,
} from '../data/agency';
import { dateOnly, dateTime } from '../data/format';
import { staffStatus, staffType } from '../data/words';
import { canManage } from '../nav';
import { useProfile } from '../shell/profile';
import {
  ActionWithReason,
  AllClear,
  Btn,
  Card,
  CardHead,
  CellStack,
  Chip,
  Drawer,
  DrawerBody,
  DrawerFoot,
  DrawerHead,
  Empty,
  Field,
  Kpi,
  KpiGrid,
  Note,
  Stack,
  Table,
  Tag,
  TRow,
  uiCss,
} from '../ui';
import { Facts, useAsync, useNotice } from './common';
import { AddAmbulance } from './addAmbulance';
import { StaffSkills, holdsSkills } from './staffSkills';

const JOIN_COLS = '1.4fr 1fr 0.9fr 1fr 0.8fr 1.3fr';
const STAFF_COLS = '1.5fr 0.9fr 0.9fr 0.9fr 0.7fr 1fr';
const VEH_COLS = '1fr 1.4fr 1.4fr 0.9fr';

type Focus = 'none' | 'join' | 'all' | 'duty' | 'vehicles';

// The roles an agency can onboard, mapped to staff_type.
const INVITE_ROLES: { v: string; label: string }[] = [
  { v: 'CLINICAL', label: 'Nurse' },
  { v: 'CARE', label: 'Attendant' },
  { v: 'LOGISTICS', label: 'Ambulance driver / crew' },
  { v: 'DOCTOR', label: 'Doctor' },
];
const roleLabel = (v: string) => INVITE_ROLES.find((r) => r.v === v)?.label ?? v;

// Copy text, and say whether it worked. navigator.clipboard exists only on a
// secure page (https, or localhost); this app is also served over plain http
// (http://62.72.57.133:8085), where it is undefined and a copy silently did
// nothing while the button said "Copied". There the older execCommand path
// still works inside a click.
async function copyText(text: string): Promise<boolean> {
  try {
    if (window.isSecureContext && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to the older path */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

const OFF_ROAD_CODES = [
  { code: 'REPAIR', label: 'Being repaired' },
  { code: 'SERVICE', label: 'Scheduled service' },
  { code: 'ACCIDENT', label: 'Accident damage' },
  { code: 'PAPERS', label: 'Papers being renewed' },
  { code: 'OTHER', label: 'Something else — say what in the note' },
] as const;
const ON_ROAD_CODES = [
  { code: 'REPAIRED', label: 'Repaired and checked' },
  { code: 'SERVICED', label: 'Service done' },
  { code: 'OTHER', label: 'Something else — say what in the note' },
] as const;

/** A trip's status, in words. */
const TRIP_WORD: Record<string, string> = {
  DISPATCHED: 'Assigned, not started',
  EN_ROUTE_TO_PICKUP: 'On the way to the patient',
  AT_PICKUP: 'At the patient',
  EN_ROUTE_TO_DROP: 'Taking the patient',
  AT_DROP: 'At the destination',
};

function vehicleTag(v: Vehicle) {
  if (v.live_trip_status)
    return (
      <Tag fg="var(--teal)" bg="var(--teal-bg)">
        On a trip
      </Tag>
    );
  if (v.status === 'ACTIVE')
    return (
      <Tag fg="var(--ok)" bg="var(--ok-bg)">
        Ready
      </Tag>
    );
  if (v.status === 'MAINTENANCE')
    return (
      <Tag fg="var(--amber)" bg="var(--amber-bg)">
        Off the road
      </Tag>
    );
  return (
    <Tag fg="var(--ink-soft)" bg="var(--surface-field)">
      {v.status === 'RETIRED' ? 'Retired' : v.status.replace(/_/g, ' ').toLowerCase()}
    </Tag>
  );
}

function equipmentWords(v: Vehicle): string {
  const kit = [v.has_oxygen && 'oxygen', v.has_ventilator && 'ventilator', v.has_defibrillator && 'defibrillator', v.has_suction && 'suction'].filter(Boolean);
  return kit.length ? kit.join(', ') : 'none recorded';
}

const DECLINE_CODES = [
  { code: 'NOT_EMPLOYED', label: 'This person does not work for us' },
  { code: 'WRONG_AGENCY', label: 'They chose the wrong agency' },
  { code: 'LEFT_ALREADY', label: 'They no longer work for us' },
  { code: 'OTHER', label: 'Something else — say what in the note' },
] as const;

const LEAVE_CODES = [
  { code: 'RESIGNED', label: 'They resigned' },
  { code: 'CONTRACT_ENDED', label: 'Their contract ended' },
  { code: 'DISMISSED', label: 'Their employment was ended' },
  { code: 'OTHER', label: 'Something else — say what in the note' },
] as const;

function statusTag(p: StaffMember) {
  const t =
    p.status === 'ACTIVE'
      ? { fg: 'var(--ok)', bg: 'var(--ok-bg)' }
      : p.status === 'SUSPENDED' || p.status === 'REJECTED'
        ? { fg: 'var(--danger)', bg: 'var(--danger-bg)' }
        : { fg: 'var(--amber)', bg: 'var(--amber-bg)' };
  return (
    <Tag fg={t.fg} bg={t.bg}>
      {staffStatus(p.status)}
    </Tag>
  );
}

function reasonOf(codes: readonly { code: string; label: string }[], code: string, note: string, max: number): string {
  const label = codes.find((c) => c.code === code)?.label ?? code;
  return (note.trim() ? `${label}: ${note.trim()}` : label).slice(0, max);
}

export function TeamScreen() {
  const { profile, reload: reloadProfile } = useProfile();
  const manage = canManage(profile.me.role);
  const team = useAsync(getTeam, []);
  const { notice, setNotice, run } = useNotice();
  const [addingAmb, setAddingAmb] = useState(false);
  const [params, setParams] = useSearchParams();
  const [typed, setTyped] = useState(params.get('q') ?? '');
  const [type, setType] = useState('ALL');
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<StaffMember | null>(null);
  const [declining, setDeclining] = useState<StaffMember | null>(null);
  const [leaving, setLeaving] = useState<StaffMember | null>(null);
  const [leftOn, setLeftOn] = useState('');
  const [focus, setFocus] = useState<Focus>('none');
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [roadChange, setRoadChange] = useState<Vehicle | null>(null);
  const navigate = useNavigate();
  const comp = useAsync(getCompliance, []);
  const joinRef = useRef<HTMLDivElement>(null);
  const staffRef = useRef<HTMLDivElement>(null);
  const vehRef = useRef<HTMLDivElement>(null);

  // Onboarding (000081): the agency adds a worker and gets a link to send them.
  const invites = useAsync(listInvites, []);
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [inName, setInName] = useState('');
  const [inPhone, setInPhone] = useState('');
  const [inRole, setInRole] = useState('CLINICAL');
  const [inEmp, setInEmp] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<StaffInvite | null>(null);
  const [inErr, setInErr] = useState('');
  // The link last copied (each row says "Copied" on its own), and whether the
  // browser refused: then the person is told to copy it by hand.
  const [copied, setCopied] = useState<string | null>(null);
  const [copyFailed, setCopyFailed] = useState<string | null>(null);
  const pendingInvites = (invites.data ?? []).filter((i) => i.status === 'PENDING');

  const resetOnboard = () => {
    setOnboardOpen(false);
    setInName(''); setInPhone(''); setInRole('CLINICAL'); setInEmp('');
    setCreated(null); setInErr(''); setCopied(null); setCopyFailed(null);
  };
  const submitInvite = async () => {
    setInErr('');
    if (!inName.trim()) { setInErr('Enter the worker’s full name.'); return; }
    if (inPhone.replace(/\D/g, '').length < 10) { setInErr('Enter a 10-digit mobile number.'); return; }
    if (!inEmp.trim()) { setInErr('Enter the employee ID.'); return; }
    setCreating(true);
    try {
      const inv = await createInvite({ phone: inPhone.trim(), full_name: inName.trim(), staff_type: inRole, agency_employee_ref: inEmp.trim() });
      setCreated(inv);
      invites.reload();
    } catch (e) {
      setInErr(e instanceof Error ? e.message : 'Could not create the invite.');
    } finally {
      setCreating(false);
    }
  };
  const copyLink = async (url: string) => {
    const ok = await copyText(url);
    setCopied(ok ? url : null);
    setCopyFailed(ok ? null : url);
    if (ok) window.setTimeout(() => setCopied((c) => (c === url ? null : c)), 1800);
  };
  const revoke = (id: string) => {
    void run(() => revokeInvite(id), 'Invite revoked.').then(() => invites.reload());
  };

  const staff = useMemo(() => team.data?.staff ?? [], [team.data]);
  const vehicles = team.data?.vehicles ?? [];
  const pending = staff.filter((p) => !p.confirmed && (p.status === 'PENDING_VERIFICATION' || p.status === 'ACTIVE'));
  const members = staff.filter((p) => p.confirmed && p.status !== 'LEFT');
  const types = Array.from(new Set(members.map((p) => p.staff_type)));
  const q = typed.trim().toLowerCase();
  const shown = members.filter(
    (p) =>
      (type === 'ALL' || p.staff_type === type) &&
      (focus !== 'duty' || p.on_duty) &&
      (!q || [p.full_name, p.phone_e164, p.staff_code, p.employee_ref ?? ''].join(' ').toLowerCase().includes(q)),
  );
  const open = staff.find((p) => p.public_id === openId) ?? null;
  const vehicle = vehicles.find((v) => v.public_id === vehicleId) ?? null;
  const vehiclePapers = comp.data?.vehicles.find((v) => v.public_id === vehicleId)?.documents ?? [];
  const paperName = (code: string) => comp.data?.document_types.find((t) => t.code === code)?.name ?? code.replace(/_/g, ' ').toLowerCase();

  // A tile: light it, filter where it filters, and bring its list into view.
  const go = (f: Focus) => {
    const next = focus === f ? 'none' : f;
    setFocus(next);
    if (next === 'none') return;
    if (next === 'all' || next === 'duty') setType('ALL');
    const target = next === 'join' ? joinRef : next === 'vehicles' ? vehRef : staffRef;
    window.setTimeout(() => target.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };
  const lit = (f: Focus) => (focus === f ? { bg: 'var(--teal-bg)', bd: 'var(--teal)', c: 'var(--teal)' } : undefined);

  const refresh = () => {
    team.reload();
    void reloadProfile();
  };

  return (
    <Stack>
      <KpiGrid>
        <Kpi
          label="Asking to join"
          value={pending.length}
          note="confirm the people who work for you"
          color={pending.length ? 'var(--amber)' : undefined}
          onClick={() => go('join')}
          tone={lit('join')}
        />
        <Kpi label="Confirmed staff" value={members.length} note="can be sent to your jobs" onClick={() => go('all')} tone={lit('all')} />
        <Kpi
          label="On duty"
          value={members.filter((p) => p.on_duty).length}
          note={focus === 'duty' ? 'showing only them below — click to clear' : 'switched on in the partner app'}
          onClick={() => go('duty')}
          tone={lit('duty')}
        />
        <Kpi
          label="Ambulances"
          value={vehicles.length}
          note={`${vehicles.filter((v) => v.status === 'ACTIVE' && !v.live_trip_status).length} ready · ${vehicles.filter((v) => v.live_trip_status).length} on a trip`}
          onClick={() => go('vehicles')}
          tone={lit('vehicles')}
        />
      </KpiGrid>

      {notice}
      {team.error ? <Note tone="danger">Your team could not be read: {team.error}</Note> : null}

      {/* ------------------------------------------------------------ onboard a worker */}
      {manage ? (
        <Card>
          <CardHead
            title="Onboard a worker"
            sub="Add your nurse, attendant, driver or doctor, and send them the link. They sign up with just their number — their details and your agency are filled in for them."
            right={<Btn variant="primary" onClick={() => { setCreated(null); setInErr(''); setOnboardOpen(true); }}>Onboard a worker</Btn>}
          />
          {invites.loading && !invites.data ? (
            <Empty>Reading invites…</Empty>
          ) : pendingInvites.length === 0 ? (
            <AllClear title="No invites waiting" sub="Onboard a worker to create an invite link; it appears here until they sign up." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pendingInvites.map((i) => (
                <div key={i.public_id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 12, padding: '12px 14px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ font: '700 14px/1.3 var(--font)' }}>{i.full_name} <span className={uiCss.sub}>· {roleLabel(i.staff_type)}</span></div>
                    <div className={uiCss.sub}>{i.phone} · ID {i.agency_employee_ref} · invited, not yet signed up</div>
                  </div>
                  {i.accept_url ? <Btn size="sm" onClick={() => void copyLink(i.accept_url!)}>{copied === i.accept_url ? 'Copied ✓' : 'Copy link'}</Btn> : null}
                  <Btn size="sm" variant="danger" onClick={() => revoke(i.public_id)}>Revoke</Btn>
                </div>
              ))}
            </div>
          )}
        </Card>
      ) : null}

      {/* ------------------------------------------------------------ join requests */}
      <div ref={joinRef} style={{ scrollMarginTop: 12 }} />
      <Card>
        <CardHead
          title="Asking to join"
          sub="People who named your agency in the Apkicare Partner app. Confirm only those who really work for you — the platform still checks their papers."
        />
        {team.loading && !team.data ? (
          <Empty>Reading your team…</Empty>
        ) : pending.length === 0 ? (
          <AllClear title="Nobody is waiting" sub="People appear here when they sign up in the partner app and name your agency." />
        ) : (
          <Table cols={JOIN_COLS} head={['Name', 'Works as', 'Phone', 'Your employee ID', 'Asked on', '']} minWidth={820}>
            {pending.map((p) => (
              <TRow key={p.public_id} cols={JOIN_COLS}>
                <CellStack top={<strong>{p.full_name || '—'}</strong>} sub={staffStatus(p.status)} />
                <span>{staffType(p.staff_type)}</span>
                <span className={uiCss.mono}>{p.phone_e164 || '—'}</span>
                <span>{p.employee_ref || '—'}</span>
                <span className={uiCss.sub}>{dateOnly(p.joined_on)}</span>
                <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  {manage ? (
                    <>
                      <Btn size="sm" variant="ok" onClick={() => setConfirming(p)}>
                        Confirm
                      </Btn>
                      <Btn size="sm" variant="danger" onClick={() => setDeclining(p)}>
                        Not ours
                      </Btn>
                    </>
                  ) : (
                    <span className={uiCss.sub}>An owner or admin confirms.</span>
                  )}
                </span>
              </TRow>
            ))}
          </Table>
        )}
      </Card>

      {/* ------------------------------------------------------------ staff */}
      <div ref={staffRef} style={{ scrollMarginTop: 12 }} />
      <Card>
        <CardHead
          title={focus === 'duty' ? 'Your staff on duty now' : 'Your staff'}
          sub={
            focus === 'duty'
              ? 'Only the people switched on in the partner app. Click the On duty tile again to see everyone.'
              : `${members.length} ${members.length === 1 ? 'person' : 'people'} confirmed as working for you.`
          }
        />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', padding: '0 18px 12px' }}>
          <div className={uiCss.chips}>
            <Chip on={type === 'ALL'} onClick={() => setType('ALL')}>
              Everyone
            </Chip>
            {types.map((t) => (
              <Chip key={t} on={type === t} onClick={() => setType(t)}>
                {staffType(t)}
              </Chip>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <input
            className={uiCss.input}
            style={{ maxWidth: 280 }}
            value={typed}
            placeholder="Name, phone, code or employee ID"
            aria-label="Search your staff"
            onChange={(e) => {
              setTyped(e.target.value);
              const next = new URLSearchParams(params);
              if (e.target.value.trim()) next.set('q', e.target.value);
              else next.delete('q');
              setParams(next, { replace: true });
            }}
          />
        </div>
        {members.length === 0 ? (
          <Empty>{team.loading ? 'Reading…' : 'No confirmed staff yet.'}</Empty>
        ) : shown.length === 0 ? (
          <Empty>{focus === 'duty' ? 'Nobody is on duty right now.' : 'Nobody matches this filter.'}</Empty>
        ) : (
          <Table cols={STAFF_COLS} head={['Name', 'Works as', 'Code', 'Status', 'On duty', 'Phone']} minWidth={780}>
            {shown.map((p) => (
              <TRow key={p.public_id} cols={STAFF_COLS} onClick={() => setOpenId(p.public_id)} selected={p.public_id === openId}>
                <CellStack top={<strong>{p.full_name || '—'}</strong>} sub={p.employee_ref ? `ID ${p.employee_ref}` : undefined} />
                <span>{staffType(p.staff_type)}</span>
                <span className={uiCss.mono}>{p.staff_code || '—'}</span>
                <span>{statusTag(p)}</span>
                <span>{p.on_duty ? <Tag fg="var(--ok)" bg="var(--ok-bg)">On</Tag> : <span className={uiCss.sub}>Off</span>}</span>
                <span className={uiCss.mono}>{p.phone_e164 || '—'}</span>
              </TRow>
            ))}
          </Table>
        )}
      </Card>

      {/* ------------------------------------------------------------ vehicles */}
      <div ref={vehRef} style={{ scrollMarginTop: 12 }} />
      <Card>
        <CardHead
          title="Ambulances"
          sub="Open an ambulance to see what it carries, the trip it is on, its papers, or to take it off the road."
          right={
            manage ? (
              <Btn size="sm" variant="primary" onClick={() => setAddingAmb(true)}>
                Add ambulance
              </Btn>
            ) : undefined
          }
        />
        {vehicles.length === 0 ? (
          <Empty>No ambulances yet. Add one your agency owns, or a driver who joins you with their own ambulance brings it here.</Empty>
        ) : (
          <Table cols={VEH_COLS} head={['Registration', 'Vehicle', 'Driver / owner', 'Status']} minWidth={720}>
            {vehicles.map((v) => (
              <TRow key={v.public_id} cols={VEH_COLS} onClick={() => setVehicleId(v.public_id)} selected={v.public_id === vehicleId}>
                <span className={uiCss.monoStrong}>{v.registration_number}</span>
                <CellStack top={[v.make, v.model].filter(Boolean).join(' ') || '—'} sub={v.manufacture_year ? String(v.manufacture_year) : undefined} />
                <CellStack
                  top={v.live_trip_driver || v.owner_name || 'Your fleet'}
                  sub={v.live_trip_driver ? 'driving now' : v.owner_name ? 'brought it' : undefined}
                />
                <span>{vehicleTag(v)}</span>
              </TRow>
            ))}
          </Table>
        )}
      </Card>

      <AddAmbulance
        open={addingAmb}
        onClose={() => setAddingAmb(false)}
        onAdded={(plate) => {
          setAddingAmb(false);
          setNotice({ tone: 'ok', text: `${plate} is on your fleet. Upload its papers on Documents — it takes trips once they are approved.` });
          team.reload();
          comp.reload();
        }}
      />

      {/* ------------------------------------------------------------ the ambulance */}
      <Drawer open={vehicle !== null} onClose={() => setVehicleId(null)} label="Ambulance">
        {vehicle ? (
          <>
            <DrawerHead onClose={() => setVehicleId(null)}>
              <div className={uiCss.label}>Ambulance</div>
              <div style={{ font: '700 19px/1.25 var(--font-mono)', marginTop: 4 }}>{vehicle.registration_number}</div>
              <div className={uiCss.cardSub}>
                {[vehicle.make, vehicle.model, vehicle.manufacture_year].filter(Boolean).join(' ') || 'Make and model not given'}
              </div>
            </DrawerHead>
            <DrawerBody>
              <Stack>
                <Facts
                  items={[
                    ['Status', vehicleTag(vehicle)],
                    ['Driver / owner', vehicle.owner_name ? `${vehicle.owner_name} (brought it)` : 'Your agency fleet'],
                    ['Equipment', equipmentWords(vehicle)],
                    ['Stretchers', vehicle.stretcher_capacity ?? '—'],
                    ['Seats', vehicle.seating_capacity ?? '—'],
                    ['Trips completed', vehicle.trips_completed],
                    ['Last trip', vehicle.last_trip_at ? dateTime(vehicle.last_trip_at) : 'None yet'],
                  ]}
                />
                {vehicle.live_trip_status ? (
                  <Card>
                    <CardHead
                      title="On a trip now"
                      sub={`${TRIP_WORD[vehicle.live_trip_status] ?? vehicle.live_trip_status.replace(/_/g, ' ').toLowerCase()}${vehicle.live_trip_driver ? ` · ${vehicle.live_trip_driver} driving` : ''}`}
                      right={
                        vehicle.live_trip_item_public_id ? (
                          <Btn size="sm" onClick={() => navigate(`/assign/${encodeURIComponent(vehicle.live_trip_item_public_id ?? '')}`)}>
                            Open the job
                          </Btn>
                        ) : undefined
                      }
                    />
                  </Card>
                ) : null}
                <Card>
                  <CardHead
                    title="Papers"
                    sub="Registration, insurance, fitness, permit and pollution. An expired paper keeps it off your jobs."
                    right={
                      <Btn size="sm" onClick={() => navigate('/documents')}>
                        Upload
                      </Btn>
                    }
                  />
                  {comp.error ? (
                    <div style={{ padding: '0 18px 14px' }}>
                      <Note tone="danger">Its papers could not be read: {comp.error}</Note>
                    </div>
                  ) : !comp.data ? (
                    <Empty>Reading…</Empty>
                  ) : vehiclePapers.length === 0 ? (
                    <Empty>No papers sent yet.</Empty>
                  ) : (
                    <div>
                      {vehiclePapers.map((d) => {
                        const expired = d.expires_on ? new Date(`${d.expires_on}T23:59:59+05:30`).getTime() < Date.now() : false;
                        return (
                          <div key={d.public_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderTop: '1px solid var(--line-soft)' }}>
                            <span className={uiCss.grow}>
                              <span style={{ display: 'block', font: '700 13px var(--font-ui)', color: 'var(--ink)' }}>{paperName(d.document_type_code)}</span>
                              <span className={uiCss.sub}>{d.expires_on ? `${expired ? 'Expired' : 'Valid until'} ${dateOnly(d.expires_on)}` : 'No expiry'}</span>
                            </span>
                            {d.status === 'APPROVED' && !expired ? (
                              <Tag fg="var(--ok)" bg="var(--ok-bg)">
                                Approved
                              </Tag>
                            ) : d.status === 'APPROVED' || d.status === 'EXPIRED' ? (
                              <Tag fg="var(--danger)" bg="var(--danger-bg)">
                                Expired
                              </Tag>
                            ) : d.status === 'REJECTED' ? (
                              <Tag fg="var(--danger)" bg="var(--danger-bg)">
                                Not accepted
                              </Tag>
                            ) : (
                              <Tag fg="#2E6FC4" bg="#E5F0FB">
                                {d.status === 'SUPERSEDED' ? 'Replaced' : 'Waiting for review'}
                              </Tag>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </Stack>
            </DrawerBody>
            {manage && vehicle.status !== 'RETIRED' ? (
              <DrawerFoot>
                <div style={{ flex: 1 }} />
                {vehicle.status === 'MAINTENANCE' ? (
                  <Btn variant="ok" onClick={() => setRoadChange(vehicle)}>
                    Put back on the road
                  </Btn>
                ) : (
                  <Btn variant="danger" onClick={() => setRoadChange(vehicle)}>
                    Take off the road
                  </Btn>
                )}
              </DrawerFoot>
            ) : null}
          </>
        ) : null}
      </Drawer>

      <ActionWithReason
        open={roadChange !== null}
        onClose={() => setRoadChange(null)}
        kicker={roadChange?.status === 'MAINTENANCE' ? 'Back on the road' : 'Off the road'}
        title={
          roadChange?.status === 'MAINTENANCE'
            ? `Put ${roadChange?.registration_number ?? 'this ambulance'} back on the road?`
            : `Take ${roadChange?.registration_number ?? 'this ambulance'} off the road?`
        }
        desc={
          roadChange?.status === 'MAINTENANCE'
            ? 'It can be given to your jobs again from now.'
            : 'While it is off the road it is not given to any job — by you or by the platform. Refused while it is on a trip.'
        }
        codes={roadChange?.status === 'MAINTENANCE' ? ON_ROAD_CODES : OFF_ROAD_CODES}
        okLabel={roadChange?.status === 'MAINTENANCE' ? 'Put back on the road' : 'Take off the road'}
        okTone={roadChange?.status === 'MAINTENANCE' ? 'ok' : 'danger'}
        onConfirm={({ code, note }) => {
          const v = roadChange;
          setRoadChange(null);
          if (!v) return;
          const back = v.status === 'MAINTENANCE';
          void run(
            () => setVehicleStatus(v.public_id, back ? 'ACTIVE' : 'MAINTENANCE', reasonOf(back ? ON_ROAD_CODES : OFF_ROAD_CODES, code, note, 200)),
            back ? `${v.registration_number} is back on the road.` : `${v.registration_number} is off the road.`,
          ).then(() => team.reload());
        }}
      />

      {/* ------------------------------------------------------------ the person */}
      <Drawer open={open !== null} onClose={() => setOpenId(null)} label="Staff member">
        {open ? (
          <>
            <DrawerHead onClose={() => setOpenId(null)}>
              <div className={uiCss.label}>{staffType(open.staff_type)}</div>
              <div style={{ font: '700 19px/1.25 var(--font-display)', marginTop: 4 }}>{open.full_name || 'Name not given'}</div>
              <div className={uiCss.cardSub}>{open.phone_e164 || '—'}</div>
            </DrawerHead>
            <DrawerBody>
              <Stack>
                <Facts
                  items={[
                    ['Status', statusTag(open)],
                    ['On duty', open.on_duty ? 'Yes' : 'No'],
                    ['Code', open.staff_code || '—'],
                    ['Your employee ID', open.employee_ref || '—'],
                    ['Joined', dateOnly(open.joined_on)],
                    ['Confirmed by you', open.confirmed ? 'Yes' : 'Not yet'],
                  ]}
                />
                <Note>
                  Their papers and qualifications are checked by the platform team. A person whose papers are missing or expired is
                  not offered for your jobs until they are renewed.
                </Note>
                {holdsSkills(open.staff_type) ? <StaffSkills key={open.public_id} staffId={open.public_id} name={open.full_name} manage={manage} /> : null}
              </Stack>
            </DrawerBody>
            {manage ? (
              <DrawerFoot>
                <Btn
                  onClick={() =>
                    void run(() => withdrawDeparture(open.public_id), 'The recorded leaving was withdrawn.').then(refresh)
                  }
                >
                  Withdraw a recorded leaving
                </Btn>
                <div style={{ flex: 1 }} />
                <Btn variant="danger" onClick={() => setLeaving(open)}>
                  Record leaving
                </Btn>
              </DrawerFoot>
            ) : null}
          </>
        ) : null}
      </Drawer>

      <ActionWithReason
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        kicker="Confirm a team member"
        title={`${confirming?.full_name || 'This person'} works for you?`}
        desc={confirming ? `${staffType(confirming.staff_type)} · ${confirming.phone_e164 || 'no phone'}${confirming.employee_ref ? ` · employee ID ${confirming.employee_ref}` : ''}` : ''}
        codes={[]}
        impact="Once confirmed you can send them to your jobs, and they see the work you give them in the partner app. Their papers are still checked by the platform team."
        okLabel="Confirm"
        okTone="ok"
        onConfirm={() => {
          const p = confirming;
          setConfirming(null);
          if (!p) return;
          void run(() => confirmStaff(p.public_id), `${p.full_name || 'They'} ${p.full_name ? 'is' : 'are'} confirmed as your staff.`).then(refresh);
        }}
      />

      <ActionWithReason
        open={declining !== null}
        onClose={() => setDeclining(null)}
        kicker="Decline a join request"
        title={`${declining?.full_name || 'This person'} does not work for you?`}
        desc="Their sign-up is closed and they are told your reason. They may sign up again — on their own, or with another agency."
        codes={DECLINE_CODES}
        okLabel="Decline"
        okTone="danger"
        onConfirm={({ code, note }) => {
          const p = declining;
          setDeclining(null);
          if (!p) return;
          void run(() => declineStaff(p.public_id, reasonOf(DECLINE_CODES, code, note, 200)), `${p.full_name || 'Their'}'s request was declined.`).then(refresh);
        }}
      />

      <ActionWithReason
        open={leaving !== null}
        onClose={() => {
          setLeaving(null);
          setLeftOn('');
        }}
        kicker="Record leaving"
        title={`${leaving?.full_name || 'This person'} is leaving`}
        desc="From their last day they are no longer sent anywhere."
        codes={LEAVE_CODES}
        fields={
          <Field label="Last working day (leave empty for today)">
            <input className={uiCss.input} type="date" value={leftOn} onChange={(e) => setLeftOn(e.target.value)} />
          </Field>
        }
        impact="Jobs already given to them after their last day must be given to someone else; a leaving is refused while they hold one."
        okLabel="Record leaving"
        okTone="danger"
        onConfirm={({ code, note }) => {
          const p = leaving;
          const day = leftOn;
          setLeaving(null);
          setLeftOn('');
          if (!p) return;
          void run(() => recordDeparture(p.public_id, reasonOf(LEAVE_CODES, code, note, 200), day || undefined), `${p.full_name || 'Their'}'s leaving is recorded.`).then(() => {
            setOpenId(null);
            refresh();
          });
        }}
      />

      <Drawer open={onboardOpen} onClose={resetOnboard} label="Onboard a worker">
        <DrawerHead onClose={resetOnboard}>
          <div className={uiCss.label}>Onboarding</div>
          <div style={{ font: '700 19px/1.25 var(--font-display)', marginTop: 4 }}>Add a worker to your team</div>
        </DrawerHead>
        <DrawerBody>
          {created ? (
            <Stack>
              <Note tone="ok">Invite created for {created.full_name}. Send them the link — they sign up with just their number, and their details are filled in.</Note>
              {created.accept_url ? (
                <Field label="Invite link">
                  <input className={uiCss.input} readOnly value={created.accept_url} onFocus={(e) => e.currentTarget.select()} />
                </Field>
              ) : (
                <Note>Share this code with the worker: {created.token}</Note>
              )}
              {created.accept_url && copyFailed === created.accept_url ? (
                <Note tone="amber">This browser would not copy it. Click the link above, then press Ctrl+C (or long-press and Copy on a phone).</Note>
              ) : null}
              <div style={{ display: 'flex', gap: 8 }}>
                {created.accept_url ? (
                  <Btn variant="primary" onClick={() => void copyLink(created.accept_url!)}>{copied === created.accept_url ? 'Copied ✓' : 'Copy link'}</Btn>
                ) : null}
                <Btn onClick={() => { setCreated(null); setInName(''); setInPhone(''); setInEmp(''); }}>Onboard another</Btn>
              </div>
            </Stack>
          ) : (
            <Stack>
              <Field label="Full name">
                <input className={uiCss.input} value={inName} onChange={(e) => setInName(e.target.value)} placeholder="As on their certificate or licence" maxLength={120} />
              </Field>
              <Field label="Mobile number">
                <input className={uiCss.input} value={inPhone} onChange={(e) => setInPhone(e.target.value)} placeholder="10-digit mobile" inputMode="numeric" maxLength={14} />
              </Field>
              <Field label="Role">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {INVITE_ROLES.map((r) => (
                    <Btn key={r.v} size="sm" variant={inRole === r.v ? 'primary' : 'plain'} onClick={() => setInRole(r.v)}>{r.label}</Btn>
                  ))}
                </div>
              </Field>
              <Field label="Employee ID">
                <input className={uiCss.input} value={inEmp} onChange={(e) => setInEmp(e.target.value)} placeholder="So your agency recognises them" maxLength={64} />
              </Field>
              {inErr ? <Note tone="danger">{inErr}</Note> : null}
            </Stack>
          )}
        </DrawerBody>
        {!created ? (
          <DrawerFoot>
            <div style={{ flex: 1 }} />
            <Btn variant="primary" disabled={creating} onClick={() => void submitInvite()}>{creating ? 'Creating…' : 'Create invite link'}</Btn>
          </DrawerFoot>
        ) : null}
      </Drawer>
    </Stack>
  );
}
